import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft, Check, Heart, Sparkles } from "lucide-react";
import { getAssessment, submitAssessment } from "@/lib/clientApi";

export default function Assessment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState("intro"); // intro | questions | done
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getAssessment(token);
        setData(d);
        if (d.completed) { setPhase("done"); setResult({ already: true }); }
      } catch (e) {
        setError(true);
      }
    })();
  }, [token]);

  const choose = (optionIdx) => {
    const next = [...answers];
    next[idx] = optionIdx;
    setAnswers(next);
  };

  const advance = async () => {
    if (idx + 1 < data.questions.length) {
      setIdx((i) => i + 1);
    } else {
      setSubmitting(true);
      try {
        const r = await submitAssessment(token, answers);
        setResult(r);
        setPhase("done");
      } catch (e) {
        setError(true);
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (error) return <Centered><p className="font-display text-2xl font-bold text-slate-700 mb-2">This link isn't valid</p><p className="text-slate-500 font-semibold">Please check with your adviser for a fresh link.</p></Centered>;
  if (!data) return <Centered><Loader2 className="w-10 h-10 text-[#29ABE2] animate-spin" /></Centered>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl floaty" />
      <div className="pointer-events-none absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-indigo-200/30 blur-3xl floaty" style={{ animationDelay: "2s" }} />

      {/* top brand */}
      <div className="relative max-w-xl mx-auto px-5 pt-8 flex justify-center">
        <img src="/eyb-logo-full.png" alt="EYB Consulting" className="h-9 w-auto" />
      </div>

      <div className="relative max-w-xl mx-auto px-5 pt-8 pb-24">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="text-center">
              <div className="text-6xl mb-4">👋</div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#005580] mb-3">
                Hi{data.client_name ? ` ${data.client_name.split(" ")[0]}` : ""}, let's get to know how you work.
              </h1>
              <p className="text-slate-500 font-semibold text-lg leading-relaxed mb-2">
                This is a quick, friendly check-in{data.business_name ? ` about ${data.business_name}` : ""}. It's not a test, and there are no right or wrong answers.
              </p>
              <p className="text-slate-500 font-semibold leading-relaxed mb-8">
                Just pick what feels most like you. It helps us shape everything around exactly where you're at. Takes about 2 minutes.
              </p>
              <button
                data-testid="assessment-start-btn"
                onClick={() => setPhase("questions")}
                className="inline-flex items-center gap-2 text-white font-bold rounded-2xl px-8 py-4 bg-[#29ABE2] border-b-4 border-[#005580] active:border-b-0 active:translate-y-[3px] transition-all"
              >
                Let's begin <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </motion.div>
          )}

          {phase === "questions" && (
            <motion.div key={`q-${idx}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              {/* progress */}
              <div className="flex items-center gap-3 mb-8">
                <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-full rounded-full bg-[#29ABE2]" animate={{ width: `${((idx) / data.questions.length) * 100}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-400">{idx + 1} / {data.questions.length}</span>
              </div>

              <div className="text-5xl text-center mb-4">{data.questions[idx].emoji}</div>
              <h2 className="font-display font-bold text-2xl text-slate-800 text-center mb-7 leading-snug" data-testid="assessment-question">
                {data.questions[idx].question}
              </h2>

              <div className="space-y-3">
                {data.questions[idx].options.map((opt, i) => {
                  const active = answers[idx] === i;
                  return (
                    <button
                      key={i}
                      data-testid={`assessment-option-${i}`}
                      onClick={() => choose(i)}
                      className={`w-full text-left rounded-2xl border-2 border-b-4 px-5 py-4 font-bold transition-all flex items-center justify-between gap-3 ${
                        active ? "bg-sky-50 border-sky-400 text-sky-800" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-[15px] leading-snug">{opt}</span>
                      {active && <span className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" strokeWidth={3} /></span>}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-3 mt-8">
                {idx > 0 && (
                  <button data-testid="assessment-back-btn" onClick={() => setIdx((i) => i - 1)}
                          className="rounded-2xl border-2 border-slate-200 bg-white text-slate-500 font-bold px-5 py-3.5 hover:border-slate-300 transition-colors">
                    <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                )}
                <button
                  data-testid="assessment-next-btn"
                  onClick={advance}
                  disabled={answers[idx] == null || submitting}
                  className={`flex-1 flex items-center justify-center gap-2 text-white font-bold rounded-2xl py-3.5 border-b-4 transition-all ${
                    answers[idx] != null && !submitting
                      ? "bg-[#29ABE2] border-[#005580] active:border-b-0 active:translate-y-[3px]"
                      : "bg-slate-200 border-slate-300 cursor-not-allowed"
                  }`}
                >
                  {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Finishing…</>)
                    : idx + 1 < data.questions.length ? (<>Next <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)
                    : (<>All done <Check className="w-5 h-5" strokeWidth={3} /></>)}
                </button>
              </div>
            </motion.div>
          )}

          {phase === "done" && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center pt-6">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 12 }}
                          className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-emerald-400 border-b-8 border-emerald-600 mb-6">
                <Heart className="w-12 h-12 text-white fill-white" />
              </motion.div>
              <h1 className="font-display text-4xl font-bold text-[#005580] mb-3">Thank you!</h1>
              <p className="text-slate-500 font-semibold text-lg leading-relaxed max-w-md mx-auto">
                {result?.already
                  ? "You've already completed this check. Your adviser has everything they need."
                  : "That's all we need. Your adviser at EYB Consulting will now tailor your learning to suit exactly where you're at."}
              </p>
              <div className="mt-8 inline-flex items-center gap-2 text-sky-500 font-bold">
                <Sparkles className="w-5 h-5" /> Nothing more to do here
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6">{children}</div>
);
